<script setup lang="ts">
import { isAppUsing } from '@@/bits/flip'
import sectionSorter from '@@/bits/section_sorter'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { computePostIndexFromPost } from '@@/surface/sorter'
import SurfaceLazy from '@@/vuecomponents/SurfaceLazy.vue'
import SurfacePostGroupHeaderSection from '@@/vuecomponents/SurfacePostGroupHeaderSection.vue'
import SurfacePostsDraggablePad from '@@/vuecomponents/SurfacePostsDraggablePad.vue'
import SurfacePost from '@@/vuecomponents/surface_post.vue'
import { useDragSortableInGroups } from '@@/vuecomposables/surface_posts_drag_sortable_in_groups'
import { useScrollToNewestPost } from '@@/vuecomposables/surface_scroll_to_newest_post'
import { useSurfaceSectionsDragSortable } from '@@/vuecomposables/surface_sections_drag_sortable'
import { GroupByTypes } from '@padlet/arvo'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

defineProps({
  reactionIdsByPostId: { type: Object, default: () => ({}) },
  commentIdsByPostId: { type: Object, default: () => ({}) },
})

const surfacePostsStore = useSurfacePostsStore()
const surfacePostsDragAndDropStore = useSurfacePostsDragAndDropStore()
const surfaceSectionsStore = useSurfaceSectionsStore()

const { dir, backgroundLuminance, isPostSizeStandard, isPostSizeWide } = storeToRefs(useSurfaceStore())
const { isAPostDragging, groupIdUnderPostBeingDragged } = storeToRefs(surfacePostsDragAndDropStore)
const { allSortedPostsByGroup } = storeToRefs(surfacePostsStore)
const { isASectionDragging } = storeToRefs(surfaceSectionsStore)
const { sortedGroups, wishGroupBy } = storeToRefs(useSurfaceWishArrangementStore())
const windowSizeStore = useWindowSizeStore()
const { isSmallerThanTabletPortrait, windowHeight } = storeToRefs(windowSizeStore)

const { shouldDisplayPost, shouldDisplayGroup } = surfacePostsStore
const { canShowDraggingOverlayOnGroup } = surfacePostsDragAndDropStore

const overlayHeight = computed(() => {
  const hoveredSectionQuery = `#section-${groupIdUnderPostBeingDragged.value}`
  const hoveredSectionElementHeight = document.querySelector(hoveredSectionQuery)?.getBoundingClientRect().height
  // add extra height to the overlay so it extends beyond posts and section header
  return hoveredSectionElementHeight ? hoveredSectionElementHeight + 32 : 0
})

const overlayWidth = computed(() => {
  const postElementWidth = document.querySelector('.wish-v2')?.getBoundingClientRect().width
  // add extra width to the overlay so it extends beyond posts
  return postElementWidth ? postElementWidth + 16 : 0
})

useSurfaceSectionsDragSortable({
  onDragStart: () => {
    isASectionDragging.value = true
  },
  onDragEnd: ({ groupId }) => {
    isASectionDragging.value = false
    if (groupId) {
      const sectionElement = document.querySelector(`#section-${groupId}`) as HTMLElement | undefined
      if (!sectionElement) return
      const sortResult = sectionSorter.getNewIndex(sectionElement)
      if (!sortResult) return
      surfaceSectionsStore.sortSection(sortResult)
    }
  },
})
useDragSortableInGroups({
  onDragStart: ({ groupId }) => {
    isAPostDragging.value = true
    groupIdUnderPostBeingDragged.value = groupId
  },
  onDragEnd: ({ postCid, postId, groupId }) => {
    isAPostDragging.value = false
    if (postCid && postId) {
      const sectionId = typeof groupId === 'number' && wishGroupBy.value === GroupByTypes.Section ? groupId : undefined
      const prevWish = document
        .querySelector(`#wish-${postId}`)
        ?.closest('.wish-wrapper')
        ?.previousElementSibling?.querySelector('.wish') as HTMLElement | undefined
      const nextWish = document
        .querySelector(`#wish-${postId}`)
        ?.closest('.wish-wrapper')
        ?.nextElementSibling?.querySelector('.wish') as HTMLElement | undefined
      const { sortIndex, gapSize } = computePostIndexFromPost(`#wish-${postId}`, {
        sectionId,
        prevId: prevWish?.id,
        nextId: nextWish?.id,
      })
      surfacePostsStore.sortPost({
        postCid,
        sortIndex: sortIndex ?? undefined,
        sectionId,
        gapSize,
      })
    }
    groupIdUnderPostBeingDragged.value = undefined
  },
  format: 'stream',
})

useScrollToNewestPost()

const minHeight = computed(() => {
  // Set minHeight as 1/4 of windowHeight since we assume that each post fills 1/4 of the screen's height
  return windowHeight.value / 4
})
const minWidth = computed(() => {
  const minimumPostWidthStandard = 548
  const minimumPostWidthWide = 748
  return isPostSizeStandard.value ? minimumPostWidthStandard : minimumPostWidthWide
})
</script>

<template>
  <SurfacePostsDraggablePad :dir="dir" :class="['flex', 'flex-col', 'items-center', 'space-y-8']">
    <section
      v-for="group in sortedGroups"
      v-show="shouldDisplayGroup(group)"
      :id="'section-' + group.id"
      :key="group.id"
      :data-id="group.id"
      :data-rank="group.sort_index"
      :class="[
        'flex',
        'flex-col',
        'w-full',
        'items-start tablet-portrait:items-center',
        {
          'surface-section': wishGroupBy === GroupByTypes.Section,
        },
        'relative',
      ]"
    >
      <!-- DRAG AND DROP OVERLAY -->
      <div
        v-if="canShowDraggingOverlayOnGroup(group.id)"
        :class="[
          'absolute -mt-4 z-underneath',
          'border-solid border-2 rounded-xl shadow-elevation-2',
          'pointer-events-none',
          {
            'border-grape-500 bg-modal-overlay-grape-alpha-20': backgroundLuminance === 'light',
            'border-canary-500 bg-modal-overlay-canary-alpha-20': backgroundLuminance === 'dark',
          },
        ]"
        :style="{ height: `${overlayHeight}px`, width: isSmallerThanTabletPortrait ? '100%' : `${overlayWidth}px` }"
      />
      <!-- GROUP HEADER -->
      <SurfacePostGroupHeaderSection
        v-if="wishGroupBy === GroupByTypes.Section"
        :section="group"
        :class="[
          // By design, this header max width is post width - 6px.
          // Since the parent sets align-items: center, we use
          // negative margin here to pull the header a bit to the
          // left (right in RTL).
          'tablet-portrait:-ms-1.5',
          'w-full',
          // On mobile viewport, the header extends all the way to
          // the left (right in RTL) so we need to subtract 8px
          // (equal to padding).
          isPostSizeStandard && 'tablet-portrait:max-w-[calc(540px-6px)] max-w-[calc(100%-6px-8px)]',
          isPostSizeWide && 'tablet-portrait:max-w-[calc(748px-6px)] max-w-[calc(100%-6px-24px)]',
        ]"
      />

      <!-- POSTS IN GROUP -->
      <div
        :id="`group-posts-${group.id}`"
        :data-id="group.id"
        :class="[
          'flex',
          'flex-col',
          'items-center',
          'space-y-2 tablet-portrait:space-y-4',
          'box-border',
          group.id === GroupByTypes.None ? 'mt-0' : 'mt-4',
          isPostSizeStandard && 'px-2 tablet-portrait:px-0',
          isPostSizeWide && 'px-2 tablet-portrait:px-6',
          'w-full',
          'group-posts',
        ]"
      >
        <template v-if="isAppUsing('lazyRenderPosts')">
          <template v-for="post in allSortedPostsByGroup[group.id]">
            <SurfaceLazy
              v-show="shouldDisplayPost(post.cid)"
              :key="post.cid"
              :data-cid="post.cid"
              :data-id="post.id"
              :min-height="minHeight"
              :min-width="minWidth"
              :class="['wish-wrapper', 'w-full', 'flex', 'justify-center']"
            >
              <SurfacePost
                :class="[
                  // Exclude this component from using styles in app/assets/stylesheets/3/surface/show/layouts/stream.scss
                  'wish-v2',
                  'w-full',
                  isPostSizeStandard && 'tablet-portrait:max-w-135',
                  isPostSizeWide && 'tablet-portrait:max-w-187',
                ]"
                :index="post.id"
                :post="post"
                :post-reaction-ids="post.id ? reactionIdsByPostId[post.id] : []"
                :post-comment-ids="post.id ? commentIdsByPostId[post.id] : []"
              />
            </SurfaceLazy>
          </template>
        </template>
        <template v-else>
          <div
            v-for="post in allSortedPostsByGroup[group.id]"
            v-show="shouldDisplayPost(post.cid)"
            :key="post.cid"
            :data-cid="post.cid"
            :data-id="post.id"
            :class="['wish-wrapper', 'w-full', 'flex', 'justify-center']"
          >
            <SurfacePost
              :class="[
                // Exclude this component from using styles in app/assets/stylesheets/3/surface/show/layouts/stream.scss
                'wish-v2',
                'w-full',
                isPostSizeStandard && 'tablet-portrait:max-w-135',
                isPostSizeWide && 'tablet-portrait:max-w-187',
              ]"
              :post="post"
              :post-reaction-ids="post.id ? reactionIdsByPostId[post.id] : []"
              :post-comment-ids="post.id ? commentIdsByPostId[post.id] : []"
            />
          </div>
        </template>
      </div>
    </section>

    <!-- This should stay under the same parent as the posts for sizing to work properly because it relies on stuffs like calc(25% - 16px) -->
    <div key="wish-sizer" class="wish-sizer" />
  </SurfacePostsDraggablePad>
</template>
